/**
 * Panel definitions for flex/afrr segment
 * Add your afrr-related panels here based on backend dataset configs
 */
import moment from 'moment-timezone';
import { applyCountryFilterToPanels } from './countryContext';

const SUPPLY_CURVE_DATASET_ID = 'entsoe-procured-balancing-capacity';
const SUPPLY_CURVE_TIMEZONE = 'Europe/Amsterdam';
const SUPPLY_CURVE_START_Y = 22;
const SUPPLY_CURVE_PANEL_HEIGHT = 15;
const SUPPLY_CURVE_PANEL_WIDTH = 12;

const deliveryBlocks = [
  { id: '00-04', label: '00:00-04:00', startHour: 0, durationHours: 4 },
  { id: '04-08', label: '04:00-08:00', startHour: 4, durationHours: 4 },
  { id: '08-12', label: '08:00-12:00', startHour: 8, durationHours: 4 },
  { id: '12-16', label: '12:00-16:00', startHour: 12, durationHours: 4 },
  { id: '16-20', label: '16:00-20:00', startHour: 16, durationHours: 4 },
  { id: '20-24', label: '20:00-24:00', startHour: 20, durationHours: 4 },
  { id: '00-24', label: '00:00-24:00', startHour: 0, durationHours: 24 },
];

function deliveryWindowFor(dayOffset, block) {
  const start = moment
    .tz(SUPPLY_CURVE_TIMEZONE)
    .startOf('day')
    .add(dayOffset, 'days')
    .add(block.startHour, 'hours');
  const end = start.clone().add(block.durationHours, 'hours');
  return `${start.format()}/${end.format()}`;
}

// Default to a single strict 4-hour delivery block (the day's first block).
// The supply-curve picker is restricted to a single window for now, and the
// raw data only carries 4-hour blocks (no full-day window), so a 4h block is
// the only selection that resolves to data.
function defaultDeliveryWindow(dayOffset) {
  return [deliveryWindowFor(dayOffset, deliveryBlocks[0])];
}

function supplyCurvePanel({ direction, title, titleKey, x }) {
  return {
    id: `afrr-supply-today-${direction}`,
    type: 'datasetChart',
    datasetId: SUPPLY_CURVE_DATASET_ID,
    visualizationId: 'entsoe-procured-balancing-capacity-afrr-day-supply-curves',
    labelOverrides: {
      title,
      titleKey,
      descriptionKey: 'dashboard_panels.procured_supply_curve_subtitle',
    },
    controlOverrides: {
      filters: {
        reserve_product: 'afrr',
        balance_direction: direction,
        observed_time_horizon: '4h',
        delivery_window: defaultDeliveryWindow(0),
      },
      hiddenFilters: ['balance_direction', 'country', 'reserve_product'],
      visibility: {
        filters: true,
        dateRange: false,
        granularity: false,
      },
    },
    gridPos: {
      x,
      y: SUPPLY_CURVE_START_Y,
      w: SUPPLY_CURVE_PANEL_WIDTH,
      h: SUPPLY_CURVE_PANEL_HEIGHT,
    },
  };
}

const supplyCurvePanels = [
  supplyCurvePanel({
    direction: 'up',
    title: 'aFRR Supply Curves - Up regulation',
    titleKey: 'dashboard_panels.afrr_supply_curves_up.title',
    x: 0,
  }),
  supplyCurvePanel({
    direction: 'down',
    title: 'aFRR Supply Curves - Down regulation',
    titleKey: 'dashboard_panels.afrr_supply_curves_down.title',
    x: 12,
  }),
];

const lowerRowY = SUPPLY_CURVE_START_Y + SUPPLY_CURVE_PANEL_HEIGHT;

const basePanels = [
  // First row: yesterday / today / tomorrow (3 panels, 8 cols each)
  {
    id: 'afrr-prices-yesterday',
    type: 'datasetChart',
    datasetId: 'entsoe-afrr',
    visualizationId: 'entsoe-afrr-afrr-yesterday',
    gridPos: { x: 0, y: 0, w: 8, h: 10 },
  },
  {
    id: 'afrr-prices-today',
    type: 'datasetChart',
    datasetId: 'entsoe-afrr',
    visualizationId: 'entsoe-afrr-afrr-today',
    gridPos: { x: 8, y: 0, w: 8, h: 10 },
  },
  {
    id: 'afrr-prices-tomorrow',
    type: 'datasetChart',
    datasetId: 'entsoe-afrr',
    visualizationId: 'entsoe-afrr-afrr-tomorrow',
    gridPos: { x: 16, y: 0, w: 8, h: 10 },
  },

  // Second row: last 7 days + weekly (2 panels, 12 cols each)
  {
    id: 'afrr-volumes-last-7d',
    type: 'datasetChart',
    datasetId: 'entsoe-afrr',
    visualizationId: 'entsoe-afrr-chart',
    gridPos: { x: 0, y: 10, w: 12, h: 12 },
  },
  {
    id: 'afrr-volumes-by-week',
    type: 'datasetChart',
    datasetId: 'entsoe-afrr',
    visualizationId: 'entsoe-afrr-afrr-by-week-chart',
    gridPos: { x: 12, y: 10, w: 12, h: 12 },
  },
  // Third row: today's procured capacity supply curves by direction.
  ...supplyCurvePanels,
  {
    id: 'afrr-volumes-by-month',
    type: 'datasetChart',
    datasetId: 'entsoe-afrr',
    visualizationId: 'entsoe-afrr-afrr-by-month-chart',
    gridPos: { x: 0, y: lowerRowY, w: 12, h: 15 },
  },
  {
    id: 'afrr-activated-prices',
    type: 'datasetChart',
    datasetId: 'entsoe-activated-prices',
    visualizationId: 'entsoe-activated-prices-afrr-chart',
    gridPos: { x: 12, y: lowerRowY, w: 12, h: 15 },
  },
];

export const flexAfrrPanels = ({ country } = {}) => applyCountryFilterToPanels(basePanels, country);

export const flexAfrrSyncGroups = [
  {
    id: 'afrr-prices-row',
    global: false,
    ids: ['afrr-prices-yesterday', 'afrr-prices-today', 'afrr-prices-tomorrow'],
    syncAxes: { y: true },
  },
];
